import { toast } from 'react-toastify';
import {
  LS_BRAND_DRAFT_KEY,
  LS_BRAND_DRAFT_LOGO_KEY,
} from 'features/admin/brands/constants';
import {
  brandDraftClear,
  brandDraftReplace,
  brandDraftSetLogo,
  brandDraftSetValue,
} from 'features/admin/brands/store/brandDraftSlice';
import {
  LS_CONTEXT_DATA_JSON_KEY,
  LS_DSL_SOURCE_KEY,
  LS_FIELDS_DATA_JSON_KEY,
  LS_NOTIFICATION_TEMPLATES_KEY,
  LS_STYLING_CSS_KEY,
  LS_BRAND_KEY,
  LS_TRANSLATIONS_KEY,
} from 'features/admin/protocol-editor/constants';
import {
  adminClearAllNotificationTemplates,
  adminClearAllNotificationTemplatesForProtocol,
  adminClearNotificationTemplate,
  adminSetNotificationTemplate,
} from 'features/admin/protocol-editor/store/adminNotificationTemplatesSlice';
import {
  adminApplyImportedTranslations,
  adminClearAllTranslations,
  adminSetBrand,
  adminSetTranslationForLanguage,
} from 'features/admin/protocol-editor/store/adminProtocolEditorSlice';
import { logger } from 'features/logger/logger';
import type { RootState } from './store';
import type { Middleware } from '@reduxjs/toolkit';

const saveBrandDraftLogo = (logo: string | undefined) => {
  if (logo === undefined) {
    localStorage.removeItem(LS_BRAND_DRAFT_LOGO_KEY);
    return;
  }
  try {
    localStorage.setItem(LS_BRAND_DRAFT_LOGO_KEY, logo);
  } catch (error) {
    localStorage.removeItem(LS_BRAND_DRAFT_LOGO_KEY);
    logger.warn('Unable to save the brand draft logo', { details: { error } });
    toast.warning(
      'This browser could not save the logo. It stays in the draft until you reload the page.'
    );
  }
};

export const adminProtocolEditorLocalStorageMiddleware: Middleware =
  store => next => action => {
    const previousState = store.getState() as RootState;
    const result = next(action);

    const isActionWithType = (a: unknown): a is { type: string } =>
      typeof a === 'object' && a !== null && 'type' in a;

    const isActionWithStringPayload = (
      a: unknown
    ): a is { type: string; payload: string } =>
      isActionWithType(a) && 'payload' in a && typeof a.payload === 'string';

    if (!isActionWithType(action)) {
      return result;
    }

    if (
      isActionWithStringPayload(action) &&
      action.type === 'adminProtocolEditor/adminSetDslSource'
    ) {
      localStorage.setItem(LS_DSL_SOURCE_KEY, action.payload);
    } else if (
      isActionWithStringPayload(action) &&
      action.type === 'adminProtocolEditor/adminSetFieldsDataJson'
    ) {
      localStorage.setItem(LS_FIELDS_DATA_JSON_KEY, action.payload);
    } else if (
      isActionWithStringPayload(action) &&
      action.type === 'adminProtocolEditor/adminSetContextDataJson'
    ) {
      localStorage.setItem(LS_CONTEXT_DATA_JSON_KEY, action.payload);
    } else if (
      isActionWithStringPayload(action) &&
      action.type === 'adminProtocolEditor/adminSetStylingCss'
    ) {
      localStorage.setItem(LS_STYLING_CSS_KEY, action.payload);
    } else if (adminSetBrand.match(action)) {
      if (action.payload === undefined) {
        localStorage.removeItem(LS_BRAND_KEY);
      } else {
        localStorage.setItem(LS_BRAND_KEY, JSON.stringify(action.payload));
      }
    } else if (
      brandDraftSetValue.match(action) ||
      brandDraftSetLogo.match(action) ||
      brandDraftReplace.match(action) ||
      brandDraftClear.match(action)
    ) {
      const { logo, ...fields } = (store.getState() as RootState).brandDraft;
      try {
        localStorage.setItem(LS_BRAND_DRAFT_KEY, JSON.stringify(fields));
      } catch (error) {
        logger.warn('Unable to save the brand draft', { details: { error } });
      }
      if (logo !== previousState.brandDraft.logo) {
        saveBrandDraftLogo(logo);
      }
    } else if (
      adminSetTranslationForLanguage.match(action) ||
      adminApplyImportedTranslations.match(action) ||
      adminClearAllTranslations.match(action)
    ) {
      const state = store.getState() as RootState;
      localStorage.setItem(
        LS_TRANSLATIONS_KEY,
        JSON.stringify(state.adminProtocolEditor.translationsJsons)
      );
    } else if (
      adminSetNotificationTemplate.match(action) ||
      adminClearNotificationTemplate.match(action) ||
      adminClearAllNotificationTemplatesForProtocol.match(action) ||
      adminClearAllNotificationTemplates.match(action)
    ) {
      const state = store.getState() as RootState;
      const next = state.adminNotificationTemplates.templatesByProtocol;
      if (next !== lastWrittenTemplatesByProtocol) {
        lastWrittenTemplatesByProtocol = next;
        localStorage.setItem(
          LS_NOTIFICATION_TEMPLATES_KEY,
          JSON.stringify(next)
        );
      }
    }

    return result;
  };
let lastWrittenTemplatesByProtocol: unknown = undefined;
